import { Fragment, useCallback, useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  ProgressBar,
  Badge,
  Banner,
  BlockStack,
  InlineStack,
  Button,
  Box,
  Divider,
  SkeletonBodyText,
  SkeletonDisplayText,
  Icon,
  DataTable,
  Modal,
  TextField,
  ButtonGroup,
} from '@shopify/polaris';
import {
  AlertTriangleIcon,
  CheckIcon,
  ClockIcon,
} from '@shopify/polaris-icons';
import { useTranslation, useLanguage } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';
import { isWoo } from '../lib/auth';
import { formatMoney } from '../lib/money';
import { BillingWoo } from './BillingWoo';
import {
  useBillingStatus,
  useBillingPlans,
  useSubscribe,
  useUpgradePlan,
  useDowngradePlan,
  useCancelSubscription,
  useBillingHistory,
} from '../hooks/api';
import type { BillingIntervalValue } from '../hooks/api';

// Every plan the grid can show is a plan the merchant can switch to from this
// page. There is deliberately no quote-on-request tier: an ENTERPRISE column
// whose only control was a "Contact Sales" mailto is what failed App Store
// requirement 1.2.3 on 2026-08-14. This list must match the listing's Pricing
// section, and the server (BILLING_PLANS) is the source of truth for it.
const PLAN_ORDER = ['FREE', 'STARTER', 'GROWTH', 'PRO'];

// WooCommerce uses Stripe (Checkout + Customer Portal); Shopify uses the Shopify
// App Billing flow. The Shopify billing routes are gated by verifyShopifySession,
// so WC must render BillingWoo instead. Branch before any hooks (Rules of Hooks).
export function Billing() {
  return isWoo() ? <BillingWoo /> : <BillingShopify />;
}

function BillingShopify() {
  const t = useTranslation();
  const { language } = useLanguage();
  const { data: statusData, isLoading: statusLoading, error: statusError, refetch: refetchStatus } = useBillingStatus();
  const { data: plansData, isLoading: plansLoading, error: plansError } = useBillingPlans();
  const { data: historyData } = useBillingHistory();
  const subscribeMutation = useSubscribe();

  // Which cadence the table is quoting. Initialised from the subscription the
  // shop already has (below), so an annual subscriber does not land on a page
  // showing monthly prices next to a "Current plan" badge.
  const [interval, setInterval] = useState<BillingIntervalValue>('MONTHLY');
  const [intervalTouched, setIntervalTouched] = useState(false);

  // Partner code. Shopify's app billing has no promo field of its own, so this
  // input is the ONLY way a code can ever reach appSubscriptionCreate. Collapsed
  // by default: almost nobody arrives with a code, and an always-visible
  // "discount code?" box invites everyone else to go looking for one before
  // subscribing.
  const [referralCode, setReferralCode] = useState('');
  // True only when the SERVER recognised this shop as arriving from a partner,
  // so a code the merchant typed themselves is never presented as detected.
  const [detectedCode, setDetectedCode] = useState(false);
  const upgradeMutation = useUpgradePlan();
  const downgradeMutation = useDowngradePlan();
  const cancelMutation = useCancelSubscription();
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Detect billing=success from Shopify callback redirect.
  // Runs once on mount — stable refs (refetchStatus, t) are intentionally
  // omitted to avoid re-running when those identities change.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billingError = params.get('error');
    if (params.get('billing') === 'success') {
      // Name the plan. "Subscription activated successfully" was shown after a
      // downgrade too, so a merchant who had just asked to move down had no way
      // to tell from the banner whether anything had happened - which is the
      // App Store finding of 2026-08-14, seen from the merchant's side.
      // Whitelisted, not echoed. It arrives on a URL, so anything could be in
      // it, and a banner that prints an arbitrary string is a banner an
      // attacker writes.
      const rawPlan = params.get('plan');
      const plan = rawPlan && PLAN_ORDER.includes(rawPlan) ? rawPlan : null;
      const parked = params.get('parked') === '1';
      setSuccessMessage(
        plan && parked
          ? (t.billing.scheduledSuccess || 'Confirmed. You move to {plan} at the end of the period you have already paid for.').replace('{plan}', plan)
          : plan
            ? (t.billing.planNowSuccess || 'Done. You are now on the {plan} plan.').replace('{plan}', plan)
            : (t.billing.subscriptionSuccess || 'Subscription activated successfully! Your tracking is now live.'),
      );
      refetchStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('billing') === 'declined') {
      // Declining a charge is a normal answer, not a failure. Say nothing
      // alarming; the plan table is right there if they change their mind.
      window.history.replaceState({}, '', window.location.pathname);
    } else if (billingError === 'billing_activation_failed' || billingError === 'billing_invalid_params') {
      // Slugs set by the billing callback redirect - show a human message.
      setActionError(t.billing.activationFailed || 'We could not confirm your subscription. Please try again or contact support.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planStatus = statusData?.plan;
  const subscription = statusData?.subscription;
  const recommendation = statusData?.recommendation;
  const plans = plansData?.plans || [];
  // The currency WE bill in, straight from the API. Never the merchant's shop
  // currency, and never a hardcoded sign: the table used to print 119€ on a
  // store Shopify was charging $119.
  const planCurrency = plansData?.currency || plans[0]?.currency || 'USD';
  const annualDiscountPercent = plansData?.annualDiscountPercent ?? 0;
  // Annual is only offerable once the API has said what a year costs. Without
  // this the toggle could appear against an older backend and send ANNUAL to a
  // route that would price it monthly.
  const annualAvailable = plans.some((plan) => (plan.annualPrice ?? 0) > 0);

  // Adopt the merchant's live cadence once, and only until they touch the
  // toggle: re-syncing on every status refetch would fight the click.
  const liveInterval = subscription?.interval;
  useEffect(() => {
    if (!intervalTouched && (liveInterval === 'ANNUAL' || liveInterval === 'MONTHLY')) {
      setInterval(liveInterval);
    }
  }, [liveInterval, intervalTouched]);

  // The plan the shop is ENTITLED to. Without an active subscription/trial the
  // stored tier is aspirational (default STARTER) - displaying it as "current"
  // made the tier unsubscribable (inert badge) while the limit shown was FREE's.
  const effectivePlan = planStatus?.billingActive
    ? planStatus?.plan || 'FREE'
    : planStatus?.effectivePlan || 'FREE';
  const currentPlanIndex = PLAN_ORDER.indexOf(effectivePlan);
  const featureLabels = t.billing.featureLabels as Record<string, string>;

  // Full granular feature showcase, grouped by section. `always` rows are
  // included in every plan (the product's cross-plan foundation - shown, not
  // hidden); the rest follow plan.features from the backend.
  const FEATURE_SECTIONS: Array<{ title: string; rows: Array<{ key: string; always?: boolean }> }> = [
    { title: t.billing.sectionChannels, rows: [
      { key: 'google_ads' }, { key: 'meta_capi' }, { key: 'ga4' },
      { key: 'tiktok' }, { key: 'pinterest' }, { key: 'snapchat' },
    ]},
    { title: t.billing.sectionTracking, rows: [
      { key: 'server_side_tracking', always: true },
      { key: 'one_year_cookies', always: true },
      { key: 'click_id_capture', always: true },
      { key: 'full_funnel', always: true },
      { key: 'dedup', always: true },
      { key: 'auto_backfill', always: true },
      { key: 'identity_resolution' },
      { key: 'reconciliation' },
    ]},
    { title: t.billing.sectionCompliance, rows: [
      { key: 'consent_mode', always: true },
      { key: 'cmp_autodetect', always: true },
      { key: 'gdpr', always: true },
    ]},
    { title: t.billing.sectionInsights, rows: [
      { key: 'realtime_dashboard', always: true },
      { key: 'emq_monitoring', always: true },
      { key: 'live_debugger' },
      { key: 'advanced_analytics' },
      { key: 'alerts' },
      { key: 'export_csv' },
      { key: 'webhooks_out' },
      { key: 'api_access' },
    ]},
    { title: t.billing.sectionSupport, rows: [
      { key: 'basic_support', always: true },
      { key: 'priority_support' },
      { key: 'dedicated_support' },
    ]},
  ];

  const handlePlanAction = useCallback(async (planId: string) => {
    setActionError(null);
    setSuccessMessage(null);
    const targetIndex = PLAN_ORDER.indexOf(planId);
    // Same tier, different cadence is a real change and has to be routed by the
    // cadence, not by the tier index (which is equal, so both comparisons below
    // would be false and the click would do nothing).
    const sameTier = targetIndex === currentPlanIndex;
    const isDowngrade = sameTier
      ? interval === 'MONTHLY' && subscription?.interval === 'ANNUAL'
      : targetIndex < currentPlanIndex;

    try {
      let result;
      if (isDowngrade) {
        result = await downgradeMutation.mutateAsync({ plan: planId, interval });
      } else if (subscription?.billingId) {
        result = await upgradeMutation.mutateAsync({ plan: planId, interval });
      } else {
        result = await subscribeMutation.mutateAsync({
          plan: planId,
          referralCode: referralCode.trim() || undefined,
          interval,
        });
      }

      if (result.confirmationUrl) {
        if (window.top) {
          window.top.location.href = result.confirmationUrl;
        } else {
          window.location.href = result.confirmationUrl;
        }
      } else if (result.success) {
        setSuccessMessage(('message' in result && result.message) || 'Plan updated successfully.');
        refetchStatus();
      }
    } catch (error: any) {
      setActionError(error?.message || t.billing.actionFailed);
    }
  }, [currentPlanIndex, interval, subscription, subscribeMutation, upgradeMutation, downgradeMutation, referralCode, t, refetchStatus]);

  const handleCancel = useCallback(async () => {
    setShowCancelModal(false);
    try {
      const result = (await cancelMutation.mutateAsync()) as { message?: string };
      setSuccessMessage(result?.message || 'Subscription cancelled. Your plan remains active until the end of the billing period.');
      refetchStatus();
    } catch (error: any) {
      setActionError(error?.message || t.billing.actionFailed);
    }
  }, [cancelMutation, refetchStatus, t]);

  useEffect(() => {
    const fromServer = (statusData as { referralCode?: string | null } | undefined)?.referralCode;
    if (fromServer && !referralCode) {
      setReferralCode(fromServer);
      setDetectedCode(true);
    }
  }, [statusData, referralCode]);

  const isLoading = subscribeMutation.isPending || upgradeMutation.isPending || downgradeMutation.isPending || cancelMutation.isPending;

  if (statusLoading || plansLoading) {
    return (
      <Page title={t.billing.title}>
        <Layout>
          <Layout.Section><Card><BlockStack gap="400"><SkeletonDisplayText size="small" /><SkeletonBodyText lines={4} /></BlockStack></Card></Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (statusError) {
    return (
      <Page title={t.billing.title}>
        <Layout><Layout.Section><Banner tone="critical"><p>{t.common.error}: {(statusError as Error).message}</p></Banner></Layout.Section></Layout>
      </Page>
    );
  }

  return (
    <Page title={t.billing.title} subtitle={t.billing.subtitle}>
      <Layout>
        {/* Success banner */}
        {successMessage && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>
              <p>{successMessage}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Error banners */}
        {plansError && (
          <Layout.Section><Banner tone="critical"><p>{t.billing.plansLoadError}</p></Banner></Layout.Section>
        )}
        {actionError && (
          <Layout.Section><Banner tone="critical" onDismiss={() => setActionError(null)}><p>{actionError}</p></Banner></Layout.Section>
        )}

        {/* Warning banners */}
        {planStatus?.isOverLimit && (
          <Layout.Section><Banner tone="critical" icon={AlertTriangleIcon}><p>{t.billing.warningOverLimit}</p></Banner></Layout.Section>
        )}
        {planStatus?.isNearLimit && !planStatus?.isOverLimit && (
          <Layout.Section><Banner tone="warning" icon={AlertTriangleIcon}><p>{t.billing.warningNearLimit}</p></Banner></Layout.Section>
        )}
        {planStatus?.trialActive && (planStatus?.trialDaysRemaining ?? 0) <= 7 && (
          <Layout.Section><Banner tone="info" icon={ClockIcon}><p>{t.billing.warningTrialEnding} {planStatus.trialDaysRemaining} {t.billing.days}.</p></Banner></Layout.Section>
        )}

        {/* Partner code, visible rather than tucked away. Shopify cannot pass
            anything through the install OAuth - verified on their own developer
            forum - so a merchant who found us in the App Store arrives with
            nothing but the code they memorised, and this field is the only place
            it can land. Missing it costs them the discount and costs the partner
            an attribution they earned.
            Only on a first subscription, and the reason is attribution rather
            than the discount: a merchant is attributed to an affiliate exactly
            once, ever (the unique constraint on AffiliateReferral.shopId), so a
            code typed later could not credit anyone. The discount itself is no
            longer lost on a plan change - it is carried across. */}
        {!subscription?.billingId && (
          <Layout.Section>
            {detectedCode ? (
              <Banner tone="success" icon={CheckIcon}>
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd">
                    <strong>{t.billing.partnerCodeApplied || 'Partner code applied'}: {referralCode}</strong>
                  </Text>
                  <Text as="p" tone="subdued">
                    {/* No duration stated here. discountPercent and
                        discountMonths are per-affiliate so a bigger creator can
                        be given a better offer without a deploy, which means
                        "your first month" is a promise this component cannot
                        keep. Shopify's approval screen shows the real amount. */}
                    {t.billing.partnerCodeAppliedHelp ||
                      'Your discount is applied before you confirm, so the price Shopify shows you already includes it.'}
                  </Text>
                </BlockStack>
              </Banner>
            ) : (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingSm">
                    {t.billing.partnerCodeLabel || 'Partner code'}
                  </Text>
                  <Text as="p" tone="subdued">
                    {t.billing.partnerCodePrompt ||
                      'If a partner gave you a code, enter it before subscribing.'}
                  </Text>
                  <Box paddingBlockStart="100">
                    <TextField
                      label={t.billing.partnerCodeLabel || 'Partner code'}
                      labelHidden
                      value={referralCode}
                      onChange={setReferralCode}
                      autoComplete="off"
                      maxLength={40}
                      placeholder={t.billing.partnerCodePlaceholder || 'Optional'}
                    />
                  </Box>
                </BlockStack>
              </Card>
            )}
          </Layout.Section>
        )}

        {/* Already subscribed: say why the field is gone rather than removing
            it silently, and say what happens to a discount that is running.
            The old wording ("applies to a first subscription only") described
            the code and the discount as the same thing, which read as "change
            plan and you lose it" - true until 2026-08-15, and no longer. */}
        {subscription?.billingId && (
          <Layout.Section>
            <Text as="p" variant="bodySm" tone="subdued">
              {t.billing.partnerCodeFirstOnly ||
                'A partner code is entered before your first subscription. A discount already running carries over when you change plan, as long as you stay on the same billing cycle.'}
            </Text>
          </Layout.Section>
        )}

        {/* Current Plan & Usage */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">{t.billing.currentPlan}</Text>
                <InlineStack gap="200">
                  <Badge tone="info">{effectivePlan}</Badge>
                  {planStatus?.trialActive && <Badge tone="attention">{t.billing.trialActive}</Badge>}
                </InlineStack>
              </InlineStack>

              {/* The <Box> is load-bearing. Polaris ships `.Polaris-Icon` with
                  `margin: auto`, and an auto margin on a flex item absorbs all
                  free space and overrides justify-content entirely - so a bare
                  <Icon> in an InlineStack drifts to the middle and shoves its
                  own label to the far edge, which is exactly how this row read:
                  a clock floating mid-card with "9 days remaining" pinned
                  right. SetupCard.tsx already wraps every icon this way; these
                  were the ones that got missed. */}
              {planStatus?.trialActive && (
                <InlineStack gap="150" blockAlign="center" wrap={false}>
                  <Box><Icon source={ClockIcon} tone="info" /></Box>
                  <Text as="span" tone="subdued">
                    {planStatus.trialDaysRemaining} {t.billing.days} {t.billing.trialDaysRemaining}
                  </Text>
                </InlineStack>
              )}

              {/* A downgrade Shopify holds to the next billing cycle. Saying so
                  is the point: the merchant goes on paying, and getting, the
                  plan above until that date. The app used to switch them the
                  moment they approved, contradicting the approval screen they
                  had just read. */}
              {subscription?.pendingPlan && subscription.pendingPlanAt && (
                <InlineStack gap="150" blockAlign="center" wrap={false}>
                  <Box><Icon source={ClockIcon} tone="subdued" /></Box>
                  <Text as="span" tone="subdued">
                    {(t.billing.scheduledChange
                      || 'Changing to {plan} on {date}. Until then you keep {current}.')
                      .replace('{plan}', subscription.pendingPlan)
                      .replace('{current}', effectivePlan)
                      .replace(
                        '{date}',
                        new Date(subscription.pendingPlanAt).toLocaleDateString(localeMap[language]),
                      )}
                  </Text>
                </InlineStack>
              )}

              <Divider />

              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">{t.billing.ordersUsed}</Text>
                  <Text as="span" fontWeight="semibold">
                    {planStatus?.ordersUsed?.toLocaleString(localeMap[language]) || 0}
                    {planStatus?.ordersLimit !== -1 && (
                      <Text as="span" tone="subdued"> / {planStatus?.ordersLimit?.toLocaleString(localeMap[language])}</Text>
                    )}
                  </Text>
                </InlineStack>

                {planStatus?.ordersLimit !== -1 && (
                  <Box paddingBlockStart="100">
                    <ProgressBar
                      progress={Math.min(100, planStatus?.usagePercentage || 0)}
                      tone={planStatus?.isOverLimit ? 'critical' : planStatus?.isNearLimit ? 'highlight' : 'primary'}
                      size="small"
                    />
                    <Box paddingBlockStart="100">
                      <Text as="span" tone="subdued" variant="bodySm">
                        {planStatus?.usagePercentage?.toFixed(1)}% {t.billing.ofLimit}
                      </Text>
                    </Box>
                  </Box>
                )}
                {planStatus?.ordersLimit === -1 && (
                  <Text as="span" tone="success">{t.billing.unlimited}</Text>
                )}
              </BlockStack>

              {/* Cancel subscription button */}
              {subscription?.billingId && planStatus?.plan !== 'FREE' && (
                <>
                  <Divider />
                  <InlineStack align="end">
                    <Button tone="critical" variant="plain" onClick={() => setShowCancelModal(true)}>
                      {t.billing.cancelSubscription || 'Cancel subscription'}
                    </Button>
                  </InlineStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Upgrade recommendation */}
        {recommendation && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">{t.billing.recommendation}</Text>
                <Text as="p" tone="subdued">{recommendation.reason}</Text>
                {recommendation.savings && <Badge tone="success">{recommendation.savings}</Badge>}
                <Button variant="primary" onClick={() => handlePlanAction(recommendation.recommendedPlan)} loading={isLoading}>
                  {(t.billing.upgradeToRecommended || 'Upgrade to {plan}').replace('{plan}', recommendation.recommendedPlan)}
                </Button>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Plan Comparison Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                <Text as="h2" variant="headingMd">{t.billing.plans}</Text>
                {annualAvailable && (
                  <InlineStack gap="200" blockAlign="center">
                    <ButtonGroup variant="segmented">
                      <Button
                        pressed={interval === 'MONTHLY'}
                        onClick={() => { setIntervalTouched(true); setInterval('MONTHLY'); }}
                      >
                        {t.billing.billMonthly || 'Monthly'}
                      </Button>
                      <Button
                        pressed={interval === 'ANNUAL'}
                        onClick={() => { setIntervalTouched(true); setInterval('ANNUAL'); }}
                      >
                        {t.billing.billAnnually || 'Yearly'}
                      </Button>
                    </ButtonGroup>
                    {annualDiscountPercent > 0 && (
                      <Badge tone="success">
                        {(t.billing.savePercent || 'Save {percent}%').replace('{percent}', String(annualDiscountPercent))}
                      </Badge>
                    )}
                  </InlineStack>
                )}
              </InlineStack>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600, color: '#6b7280' }}>{t.billing.planName || 'Plan'}</th>
                      {plans.map(plan => (
                        <th key={plan.id} style={{
                          textAlign: 'center', padding: '12px 8px', fontWeight: 700,
                          color: plan.id === 'GROWTH' ? '#0ea5e9' : '#1f2937',
                          backgroundColor: plan.id === effectivePlan ? '#f0f9ff' : undefined,
                        }}>
                          {plan.name}
                          {plan.id === effectivePlan && (
                            <div style={{ fontSize: '0.7rem', color: '#22c55e', fontWeight: 600 }}>{t.billing.currentPlanBadge}</div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Price row */}
                    <tr style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
                      <td style={{ padding: '12px 8px', fontWeight: 600 }}>{t.billing.priceLabel || 'Price'}</td>
                      {plans.map(plan => (
                        <td key={plan.id} style={{ textAlign: 'center', padding: '12px 8px', fontWeight: 700, fontSize: '1.1rem' }}>
                          {plan.price === 0
                            ? formatMoney(0, planCurrency, localeMap[language])
                            : formatMoney(
                                interval === 'ANNUAL' ? plan.annualPricePerMonth : plan.price,
                                planCurrency,
                                localeMap[language],
                              )}
                          {plan.price > 0 && (
                            <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 400 }}>{t.billing.month}</div>
                          )}
                          {/* The headline figure is per month so the two cadences
                              are comparable, but the merchant is charged the whole
                              year at once - so the year's total is stated here
                              rather than left for them to discover on the invoice. */}
                          {plan.price > 0 && interval === 'ANNUAL' && (
                            <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 400, marginTop: 2 }}>
                              {(t.billing.billedYearly || 'billed {amount} per year').replace(
                                '{amount}',
                                formatMoney(plan.annualPrice, planCurrency, localeMap[language]),
                              )}
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                    {/* Events row */}
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 8px' }}>{t.billing.ordersPerMonth}</td>
                      {plans.map(plan => (
                        <td key={plan.id} style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 500 }}>
                          {plan.ordersPerMonth}
                        </td>
                      ))}
                    </tr>
                    {/* Feature rows, grouped by section - the full product, shown */}
                    {FEATURE_SECTIONS.map(section => (
                      <Fragment key={section.title}>
                        <tr>
                          <td colSpan={plans.length + 1} style={{
                            padding: '14px 8px 6px', fontWeight: 700, fontSize: '0.75rem',
                            textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280',
                            borderBottom: '1px solid #e5e7eb',
                          }}>
                            {section.title}
                          </td>
                        </tr>
                        {section.rows.map(row => (
                          <tr key={row.key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px' }}>{featureLabels[row.key] || row.key}</td>
                            {plans.map(plan => {
                              const hasFeature = row.always || plan.features.includes(row.key) || plan.features.includes('all');
                              return (
                                <td key={plan.id} style={{ textAlign: 'center', padding: '8px', color: hasFeature ? '#22c55e' : '#d1d5db' }}>
                                  {hasFeature ? '\u2713' : '\u2014'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                    {/* Action row */}
                    <tr>
                      <td style={{ padding: '16px 8px' }}></td>
                      {plans.map(plan => {
                        // "Current plan" means current TIER *and* current
                        // cadence. Without the cadence, selecting Yearly left an
                        // inert badge on the row the merchant wants to switch.
                        const isCurrentPlan =
                          effectivePlan === plan.id &&
                          (plan.price === 0 || !subscription?.billingId || subscription.interval === interval);
                        const targetIndex = PLAN_ORDER.indexOf(plan.id);
                        const sameTier = targetIndex === currentPlanIndex;
                        const isUpgrade = sameTier
                          ? interval === 'ANNUAL' && subscription?.interval !== 'ANNUAL'
                          : targetIndex > currentPlanIndex;
                        const isDowngrade = sameTier
                          ? interval === 'MONTHLY' && subscription?.interval === 'ANNUAL'
                          : targetIndex < currentPlanIndex;
                        // With no active subscription there is nothing to move
                        // up or down FROM, so every paid tier reads "Subscribe".
                        // It used to say "Downgrade" on any tier below the
                        // stored one, which is what a shop sees for its whole
                        // remaining trial after cancelling: the entitlement is
                        // still PRO, so STARTER was offered as a "Downgrade"
                        // that in fact created a first subscription. Seen live
                        // on 2026-08-12.
                        const label = !subscription?.billingId
                          ? (t.billing.subscribe || 'Subscribe')
                          : sameTier
                            ? (interval === 'ANNUAL'
                                ? (t.billing.switchToYearly || 'Switch to yearly')
                                : (t.billing.switchToMonthly || 'Switch to monthly'))
                            : isUpgrade
                              ? (t.billing.upgrade || 'Upgrade')
                              : (t.billing.downgrade || 'Downgrade');
                        // A change Shopify holds to the end of an annual period
                        // already paid for. It was only ever announced in the
                        // card at the top, so the grid went on marking the old
                        // tier as the only thing happening. Say it where the
                        // merchant is looking when they click.
                        //
                        // Shown ABOVE the button, never instead of it. A
                        // merchant with a change scheduled still has to be able
                        // to pick something else, and swallowing the only
                        // control on that column is the shape of defect that
                        // got this app rejected in the first place.
                        const isScheduled = subscription?.pendingPlan === plan.id;
                        return (
                          <td key={plan.id} style={{ textAlign: 'center', padding: '16px 8px' }}>
                            {isScheduled && (
                              <div style={{ marginBottom: 6 }}>
                                <Badge tone="attention">{t.billing.scheduledBadge || 'Scheduled'}</Badge>
                              </div>
                            )}
                            {isCurrentPlan ? (
                              <Badge tone="success">{t.billing.currentPlanBadge}</Badge>
                            ) : plan.id === 'FREE' ? (
                              // Going back to FREE = cancelling the subscription
                              // (the downgrade API only accepts paid tiers - this
                              // button used to 400 on every click).
                              //
                              // Only while there IS one to cancel. Offering it
                              // with no subscription answered "No active
                              // subscription to cancel" - a red error on the
                              // Free plan's only affordance, which is the exact
                              // shape of the finding that nearly delisted
                              // PassportEU. Reproduced live on 2026-08-12.
                              //
                              // Reachable now only AFTER a paid cycle: the
                              // merchant cancelled, keeps the tier until the
                              // period they paid for runs out, and holds no
                              // billingId meanwhile. Cancelling inside a trial
                              // moves them to Free outright (2026-08-15), so
                              // that path lands on the Current Plan badge above
                              // instead of here.
                              subscription?.billingId ? (
                                <Button
                                  size="slim"
                                  tone="critical"
                                  onClick={() => setShowCancelModal(true)}
                                  loading={isLoading}
                                >
                                  {t.billing.cancelSubscription || 'Cancel subscription'}
                                </Button>
                              ) : (
                                <Text as="span" variant="bodySm" tone="subdued">
                                  {t.billing.alreadyCancelled
                                    || 'Cancelled. Access runs to the end of the period.'}
                                </Text>
                              )
                            ) : (
                              <Button
                                size="slim"
                                variant={isUpgrade ? 'primary' : undefined}
                                tone={isDowngrade ? 'critical' : undefined}
                                onClick={() => handlePlanAction(plan.id)}
                                loading={isLoading}
                              >
                                {label}
                              </Button>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Billing History */}
        {historyData?.history && historyData.history.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">{t.billing.billingHistory}</Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'text']}
                  headings={[t.billing.historyDate || 'Date', t.billing.historyType || 'Type', t.billing.historyAmount || 'Amount', t.billing.historyStatus || 'Status']}
                  rows={historyData.history.map((entry: any) => [
                    new Date(entry.date).toLocaleDateString(localeMap[language]),
                    entry.type,
                    formatMoney(entry.amount, planCurrency, localeMap[language]),
                    entry.status,
                  ])}
                />
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Cancel Modal */}
        <Modal
          open={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          title={t.billing.cancelTitle || 'Cancel Subscription'}
          primaryAction={{ content: t.billing.confirmCancel || 'Yes, cancel', onAction: handleCancel, destructive: true, loading: cancelMutation.isPending }}
          secondaryActions={[{ content: t.billing.keepPlan || 'Keep my plan', onAction: () => setShowCancelModal(false) }]}
        >
          <Modal.Section>
            {/* Nothing has been charged during a trial, so there is no period
                for access to run out over: cancelling puts the shop back on
                Free immediately, and the copy has to say so. */}
            <Text as="p">
              {planStatus?.trialActive
                ? (t.billing.cancelConfirmationTrial
                    || 'You are still in your free trial, so nothing has been charged. Cancelling puts you back on the Free plan right away.')
                : (t.billing.cancelConfirmation
                    || 'Are you sure you want to cancel? Your plan will remain active until the end of the current billing period.')}
            </Text>
          </Modal.Section>
        </Modal>
      </Layout>
    </Page>
  );
}
